// @flow
import * as React from "react";
import { getIn } from "final-form";
import debounce from "lodash/debounce";
import type { Unsubscribe, FormApi } from "final-form";
import { Form } from "react-final-form";
import createFocusDecorator from "final-form-focus";

import countBy from "lodash/countBy";
import isEqual from "lodash/isEqual";
import every from "lodash/every";
import zipObject from "lodash/zipObject";
import map from "lodash/map";

import type { FormValidityPayload } from "../../types/form";

export type FormPageContextValueType = {|
  registerSection: (section: string, validatedFields: string[], isCounted: boolean) => () => void,
  setValidatedFieldsForSection: (section: string, validatedFields: string[]) => Promise<any>,
  validate: () => void,
  sectionsValidities: { [string]: boolean },
  form: ?FormApi
|};

export const defaultFormPageContextValue: FormPageContextValueType = {
  registerSection: () => () => {},
  setValidatedFieldsForSection: () => new Promise(resolve => resolve()),
  validate: () => {},
  sectionsValidities: {},
  form: null
};

export const FormPageContext = React.createContext<FormPageContextValueType>(
  defaultFormPageContextValue
);

type SectionFields = { [string]: string[] };

type SectionValidites = { [string]: boolean };

type FormPageRenderProps = {| form: FormApi, sections: SectionFields |};

type FormState = { [string]: {} };

type FormPageProps = {
  children: FormPageRenderProps => React.Node,
  notifyValidity: FormValidityPayload => any,
  saveFormState: (formState: FormState) => void,
  initialValues: {},
  handleSubmit: () => void,
  mutators: {}
};

type FormPageState = {
  sections: SectionFields,
  sectionsValidities: SectionValidites,
  excludedSectionIds: Array<string>
};

const getInputs = () => {
  if (typeof document === "undefined") {
    return [];
  }

  // taken directly from final-form-focus, without isFocusable function
  const formFields = Array.prototype.slice
    .call(document.forms)
    .reduce((accumulator, form) => accumulator.concat(Array.prototype.slice.call(form)), []);

  const elementsWithError = document.querySelectorAll("[data-error]");

  return [...formFields, ...elementsWithError];
};

const getInput = (inputs, errors) => {
  const focusedInput = inputs.find(input => {
    const {
      dataset: { name, error }
    } = input;

    return (name && getIn(errors, name)) || error === "true";
  });

  return {
    focus: () => {
      if (!focusedInput) {
        return;
      }

      const elementRect = focusedInput.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = absoluteElementTop - window.innerHeight / 2;
      window.scrollTo(0, middle);
    }
  };
};

const focusOnError = createFocusDecorator(getInputs, getInput);

class FormPage extends React.Component<FormPageProps, FormPageState> {
  static defaultProps = {
    children: () => null,
    notifyValidity: () => {},
    saveFormState: () => {},
    handleSubmit: () => {}
  };

  state = {
    sections: {},
    sectionsValidities: {},
    excludedSectionIds: []
  };

  formApi: null | FormApi = null;

  lastPayload: null | FormValidityPayload = null;

  unsubscribeFromFormValues: null | Unsubscribe = null;

  isUnmounted: boolean = false;

  componentWillUnmount() {
    this.isUnmounted = true;

    if (this.unsubscribeFromFormValues) {
      this.unsubscribeFromFormValues();
    }
  }

  registerSection = (section: string, validatedFields: string[], isCounted: boolean) => {
    this.setValidatedFieldsForSection(section, validatedFields);

    if (!isCounted) {
      this.addExcludedSectionId(section);
    }

    // It's necessary to put this in a 0 timeout, so it runs only after
    // Final Form registers the fields, which is done after FormSection
    // calls its componentDidMount. Otherwise, tests callback cannot find
    // the registered field and does not call the notifier.
    setTimeout(this.valueChangeSubscriber, 0);

    return () => {
      this.setState(prevState => {
        const newSections = { ...prevState.sections };
        delete newSections[section];

        return {
          sections: newSections,
          excludedSectionIds: prevState.excludedSectionIds.filter(
            sectionId => sectionId !== section
          )
        };
      });
    };
  };

  setValidatedFieldsForSection = (section: string, validatedFields: string[]) =>
    new Promise<void>(resolve =>
      this.setState(
        prevState => ({
          sections: {
            ...prevState.sections,
            [section]: validatedFields
          }
        }),
        resolve
      )
    );

  addExcludedSectionId = (sectionId: string) => {
    this.setState(state => ({ excludedSectionIds: [...state.excludedSectionIds, sectionId] }));
  };

  valueChangeSubscriber = () => {
    const { notifyValidity, saveFormState } = this.props;
    if (this.formApi === null || this.isUnmounted) {
      return;
    }

    const { values } = this.formApi.getState();

    if (values) {
      saveFormState(values);
    }

    const sectionIdsToValidate = this.getSectionIdsToValidate();
    const filteredSectionIdsToValidate = this.getSectionIdsToValidate(true);

    const payload = this.getPayload(filteredSectionIdsToValidate);

    const sectionsValidities = this.getSectionsValidities(sectionIdsToValidate);
    this.setState({ sectionsValidities });

    // bail out if no sections are validated <=> nothing to notify about
    if (payload.outOf === 0) {
      return;
    }

    // bail out if nothing changed in the payload
    if (isEqual(payload, this.lastPayload)) {
      return;
    }

    this.lastPayload = payload;

    notifyValidity(payload);
  };

  getPayload = (sectionIdsToValidate: Array<string>) => {
    const { sections } = this.state;
    const fieldSets: string[][] = sectionIdsToValidate.map((id: string) => sections[id]);

    const validatedSections = fieldSets.map(this.isSectionValid);

    const payload: FormValidityPayload = {
      valid: countBy(validatedSections).true || 0,
      outOf: fieldSets.length
    };

    return payload;
  };

  getSectionIdsToValidate = (filterExcludedSectionIds: boolean = false): Array<string> => {
    const { sections, excludedSectionIds } = this.state;

    return Object.keys(sections)
      .filter(id => (filterExcludedSectionIds ? !excludedSectionIds.includes(id) : true))
      .filter(set => set.length > 0);
  };

  getSectionsValidities = (sectionIdsToValidate: Array<string>): { [string]: boolean } => {
    const { sections } = this.state;

    return zipObject(
      sectionIdsToValidate,
      map(sectionIdsToValidate, id => this.isSectionValid(sections[id]))
    );
  };

  isSectionValid = (sectionFields: string[]) =>
    every(
      sectionFields
        .map(fieldName => this.formApi && this.formApi.getFieldState(fieldName))
        .filter(fieldState => !!fieldState)
        .map(fieldState => (fieldState ? fieldState.valid : false))
    );

  render() {
    const { children, initialValues, handleSubmit, mutators } = this.props;
    const { sections, sectionsValidities } = this.state;

    return (
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        mutators={mutators}
        decorators={[focusOnError]}
      >
        {({ form }) => {
          if (!this.formApi) {
            this.formApi = form;
          }

          if (!this.unsubscribeFromFormValues) {
            this.unsubscribeFromFormValues = form.subscribe(
              debounce(() => {
                setTimeout(this.valueChangeSubscriber, 0);
              }, 100),
              {
                values: true,
                visited: true
              }
            );
          }

          return (
            <FormPageContext.Provider
              value={{
                registerSection: this.registerSection,
                setValidatedFieldsForSection: this.setValidatedFieldsForSection,
                validate: this.valueChangeSubscriber,
                sectionsValidities,
                form
              }}
            >
              {children({ form, sections })}
            </FormPageContext.Provider>
          );
        }}
      </Form>
    );
  }
}

export default FormPage;
